/**
 * Cliente HTTP del ERP.
 *
 * Todo lo que el navegador pide a la API pasa por aquí. Tiene tres
 * responsabilidades y ninguna es opcional:
 *
 * 1. Poner el testigo CSRF en las escrituras (ADR-009). Sin él, el guard
 *    del servidor responde 403 a toda escritura hecha con la cookie.
 * 2. Renovar la sesión cuando caduca el token. El SPA **no puede saber**
 *    cuándo caduca —la cookie es `httpOnly`, no la ve—, así que la única
 *    señal disponible es el 401.
 * 3. **Renovar una sola vez aunque fallen diez peticiones a la vez.** Ver
 *    abajo: esto no es una optimización.
 */

import { ApiError } from './api-error.js';

/** Prefijo de la API. Mismo origen que el ERP, por la ADR-009. */
const BASE = '/v1';

/**
 * Ruta de renovación, **sin** el prefijo: `send()` lo añade, igual que a
 * cualquier otra. Escribirla ya prefijada la mandaba a `/v1/v1/…` y, de
 * paso, rompía la comparación que impide que una renovación fallida
 * dispare otra renovación.
 */
const SESSION_REFRESH = '/web/session/refresh';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Nombre de la cookie legible que contiene el testigo CSRF. */
const CSRF_COOKIE = 'fw_csrf';

const CSRF_HEADER = 'X-CSRF-Token';

/**
 * Lee el testigo CSRF de las cookies del documento.
 *
 * Es la única cookie de sesión que el servidor deja leer, y para esto
 * exactamente: demostrar que quien manda la petición pudo leer una cookie
 * de este sitio.
 */
export function readCsrfToken(cookieSource: string = document.cookie): string | null {
  for (const part of cookieSource.split(';')) {
    const index = part.indexOf('=');
    if (index === -1) continue;
    if (part.slice(0, index).trim() !== CSRF_COOKIE) continue;
    try {
      return decodeURIComponent(part.slice(index + 1).trim());
    } catch {
      return null;
    }
  }
  return null;
}

export interface ApiClientOptions {
  /** Inyectable para los tests; por defecto, el `fetch` del navegador. */
  fetchImpl?: typeof fetch;
  /** Inyectable para los tests; por defecto, `document.cookie`. */
  readCookies?: () => string;
  /** Se llama cuando la sesión ya no se puede recuperar. */
  onSessionLost?: () => void;
}

export class ApiClient {
  private readonly fetchImpl: typeof fetch;
  private readonly readCookies: () => string;
  private readonly onSessionLost: (() => void) | undefined;

  /**
   * Renovación en vuelo, compartida por todas las peticiones.
   *
   * **Por qué esto importa más de lo que parece.** El servidor rota el
   * refresco en cada uso y detecta el reuso: si llega un refresco ya
   * rotado, revoca *toda la familia* y echa al usuario. Es deliberadamente
   * agresivo y es correcto.
   *
   * Ahora bien: una pantalla del ERP lanza cinco peticiones a la vez. Si el
   * token caduca justo antes, las cinco reciben 401 y, sin este candado,
   * las cinco llamarían a renovar con la misma cookie. La primera rota; las
   * otras cuatro llegan con un refresco ya usado, el servidor lo lee como
   * robo y **cierra la sesión**. El usuario vería que se le cae la sesión
   * al abrir una pantalla con varias tablas, y el servidor tendría razón.
   */
  private refreshing: Promise<boolean> | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.readCookies = options.readCookies ?? (() => document.cookie);
    this.onSessionLost = options.onSessionLost;
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.send(path, init);

    // Una renovación que falla no se reintenta: sería un bucle.
    if (response.status === 401 && path !== SESSION_REFRESH) {
      const renewed = await this.refresh();
      if (renewed) {
        const retried = await this.send(path, init);
        return this.parse<T>(retried, path);
      }
      this.onSessionLost?.();
    }

    return this.parse<T>(response, path);
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  private send(path: string, init: RequestInit): Promise<Response> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers = new Headers(init.headers);

    if (init.body !== undefined && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }

    if (!SAFE_METHODS.has(method)) {
      const csrf = readCsrfToken(this.readCookies());
      if (csrf) headers.set(CSRF_HEADER, csrf);
    }

    return this.fetchImpl(`${BASE}${path}`, {
      ...init,
      headers,
      // Las cookies son la sesión: sin esto no viajan en absoluto.
      credentials: 'same-origin',
    });
  }

  /** Renueva la sesión, compartiendo una única llamada en vuelo. */
  private refresh(): Promise<boolean> {
    this.refreshing ??= this.send(SESSION_REFRESH, { method: 'POST' })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        this.refreshing = null;
      });

    return this.refreshing;
  }

  private async parse<T>(response: Response, path: string): Promise<T> {
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const body: unknown = text ? safeJson(text) : null;

    if (!response.ok) throw ApiError.from(response.status, body, path);

    return body as T;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
